import { useState, useEffect, useCallback, useRef } from 'react';
import { SlotInfo, CalDateInfo } from '../../../types/schedule.types';
import { ElementSceneInfo } from '../../../types/element.types';
import { useDataCache } from '../../../contexts/BreakdownDataCacheContext';

/**
 * 获取拍摄计划数据的 Hook
 * @param scheduleID 拍摄计划ID
 * @returns slots 数组、calDates 数组、elementRelations 数组，以及刷新函数
 */
export const useScheduleDate = (
  scheduleID: string | undefined
) => {
  const [slots, setSlots] = useState<SlotInfo[]>([]);
  const [calDates, setCalDates] = useState<CalDateInfo[]>([]);
  const [elementRelations, setElementRelations] = useState<ElementSceneInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refreshPromiseRef = useRef<{ resolve: () => void; reject: (error: any) => void } | null>(null);
  const { 
    getElementRelationCache, 
    subscribeElementRelationCache,
    getSlotCache,
    subscribeSlotCache,
    getCalDateCache,
    subscribeCalDateCache
  } = useDataCache();

  const fetchData = useCallback(() => {
    if (!scheduleID) {
      setSlots([]);
      setCalDates([]);
      setElementRelations([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. 只从缓存获取 slot 数据
      const slotCache = getSlotCache(scheduleID);
      if (slotCache?.slots && slotCache.slots.length > 0) {
        setSlots(slotCache.slots);
      } else {
        setSlots([]);
      }

      // 2. 只从缓存获取 calDate 数据
      const calDateCache = getCalDateCache(scheduleID);
      if (calDateCache?.calDates && calDateCache.calDates.length > 0) {
        setCalDates(calDateCache.calDates);
      } else {
        setCalDates([]);
      }

      // 3. 从缓存获取 ScheduleInfo，然后使用其中的 scheduleBelongBreakdownID 从缓存获取 element-scene 关系数据
      const scheduleInfo = slotCache?.scheduleInfo;
      if (scheduleInfo?.scheduleBelongBreakdownID) {
        const cachedData = getElementRelationCache(scheduleInfo.scheduleBelongBreakdownID);
        if (cachedData?.elementRelations) {
          setElementRelations(cachedData.elementRelations);
        } else {
          setElementRelations([]);
        }
      } else {
        setElementRelations([]);
      }
    } catch (err) {
      console.error('从缓存获取拍摄计划数据失败:', err);
      setError(err instanceof Error ? err.message : '从缓存获取拍摄计划数据失败');
      setSlots([]);
      setCalDates([]);
      setElementRelations([]);
      // 如果有等待刷新的 Promise，reject 它
      if (refreshPromiseRef.current) {
        refreshPromiseRef.current.reject(err);
        refreshPromiseRef.current = null;
      }
    } finally {
      setLoading(false);
      // 如果有等待刷新的 Promise，resolve 它
      if (refreshPromiseRef.current) {
        refreshPromiseRef.current.resolve();
        refreshPromiseRef.current = null;
      }
    }
  }, [scheduleID, getElementRelationCache, getSlotCache, getCalDateCache]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshTrigger]);

  // 订阅 slot 缓存更新
  useEffect(() => {
    if (!scheduleID) return;

    const updateSlotsFromCache = () => {
      const cache = getSlotCache(scheduleID);
      if (cache?.slots) {
        setSlots(cache.slots);
      }
    };

    // 立即更新一次
    updateSlotsFromCache();

    // 订阅缓存更新
    const unsubscribe = subscribeSlotCache(scheduleID, updateSlotsFromCache);
    return unsubscribe;
  }, [scheduleID, getSlotCache, subscribeSlotCache]);

  // 订阅 calDate 缓存更新
  useEffect(() => {
    if (!scheduleID) return;

    const updateCalDatesFromCache = () => {
      const cache = getCalDateCache(scheduleID);
      if (cache?.calDates) {
        setCalDates(cache.calDates);
      }
    };

    // 立即更新一次
    updateCalDatesFromCache();

    // 订阅缓存更新
    const unsubscribe = subscribeCalDateCache(scheduleID, updateCalDatesFromCache);
    return unsubscribe;
  }, [scheduleID, getCalDateCache, subscribeCalDateCache]);

  // 订阅 elementRelations 缓存更新
  useEffect(() => {
    if (!scheduleID) return;

    const updateFromCache = () => {
      // 从缓存获取 ScheduleInfo
      const slotCache = getSlotCache(scheduleID);
      const scheduleInfo = slotCache?.scheduleInfo;
      
      if (scheduleInfo?.scheduleBelongBreakdownID) {
        const cachedData = getElementRelationCache(scheduleInfo.scheduleBelongBreakdownID);
        if (cachedData?.elementRelations) {
          setElementRelations(cachedData.elementRelations);
        } else {
          setElementRelations([]);
        }
      } else {
        setElementRelations([]);
      }
    };

    // 立即更新一次
    updateFromCache();

    // 订阅缓存更新
    let unsubscribe: (() => void) | undefined;
    const slotCache = getSlotCache(scheduleID);
    const scheduleInfo = slotCache?.scheduleInfo;
    if (scheduleInfo?.scheduleBelongBreakdownID) {
      unsubscribe = subscribeElementRelationCache(scheduleInfo.scheduleBelongBreakdownID, updateFromCache);
    }

    return () => {
      unsubscribe?.();
    };
  }, [scheduleID, getElementRelationCache, getSlotCache, subscribeElementRelationCache]);

  const refresh = useCallback(async () => {
    return new Promise<void>((resolve, reject) => {
      // 保存 resolve 和 reject，在 fetchData 完成后调用
      refreshPromiseRef.current = { resolve, reject };
      setRefreshTrigger(prev => prev + 1);
    });
  }, []);

  return {
    slots,
    calDates,
    elementRelations,
    loading,
    error,
    refresh
  };
};

